import { Fragment } from 'react';
import { Form } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import { ArrowRepeat } from 'react-bootstrap-icons';

import { Info } from 'components/base/Info';
import { EditNumber } from 'components/forms/EditNumber';
import { Field } from 'components/forms/Field';

import type { EditableNodeDefinition } from './EditableNodeDefinition';
import { ReturnCodeChips } from './ReturnCodeChips';
import type { AutoRestartSegment, AutoRestartTimeline } from './autoRestartView';

import './AutoRestartCard.scss';

const renderSummary = (segments: readonly AutoRestartSegment[]) =>
  segments.map((segment, index) => (segment.strong ? <strong key={index}>{segment.text}</strong> : <Fragment key={index}>{segment.text}</Fragment>));

const Arrow = () => (
  <span className="auto-restart__arrow" aria-hidden>
    →
  </span>
);

// The retry cycle shown once with a repeat badge. Decorative: the summary sentence above carries the same information for
// assistive tech, so the whole strip is aria-hidden.
const Timeline = ({ timeline }: { timeline: AutoRestartTimeline }) => (
  <div className="auto-restart__flow" aria-hidden>
    <span className="auto-restart__node auto-restart__node--fail">
      <span className="auto-restart__dot" />
      {timeline.failLabel}
    </span>
    <Arrow />
    <span className="auto-restart__loop">
      {timeline.showWait && (
        <>
          <span className="auto-restart__node auto-restart__node--wait">
            <span className="auto-restart__dot" />
            {timeline.waitLabel}
          </span>
          <Arrow />
        </>
      )}
      <span className="auto-restart__node auto-restart__node--retry">
        <span className="auto-restart__dot" />
        {timeline.retryLabel}
      </span>
      {timeline.badge && (
        <span className="auto-restart__badge">
          <ArrowRepeat aria-hidden />
          {timeline.badge}
        </span>
      )}
    </span>
    <Arrow />
    <span className="auto-restart__node auto-restart__node--final">{timeline.finalLabel}</span>
  </div>
);

interface Props {
  readonly entity: EditableNodeDefinition;
}

/**
 * The Automatic-restart card on the node-definition Properties tab: an enable switch in the header with a status pill, and
 * (when on) a live plain-language summary, a retry timeline, an optional return-code filter, and the max/delay inputs. A pure
 * `observer` over the definition's auto-restart fields — every control reads a getter and writes a bound setter, no local
 * form state; the summary/timeline come from the model's `autoRestartView` computed. Shown only for non-flow node types (the
 * caller gates on `type`).
 */
export const AutoRestartCard = observer<Props>(({ entity }) => {
  const enabled = entity.autoRestartEnabled;
  const view = entity.autoRestartView;

  return (
    <div className={`auto-restart${enabled ? '' : ' auto-restart--off'}`}>
      <header className="auto-restart__head">
        <span className="auto-restart__icon" aria-hidden>
          <ArrowRepeat />
        </span>
        <span className="auto-restart__titles">
          <span className="auto-restart__title">Automatic restart</span>
          <span className="auto-restart__subtitle">{enabled ? 'Retry this node when its run fails' : 'A failed run is final'}</span>
        </span>
        <span className="auto-restart__controls">
          <span className={`auto-restart__pill auto-restart__pill--${enabled ? 'fail' : 'off'}`}>
            <span className="auto-restart__dot" />
            {enabled ? 'Triggers on: Failed' : 'Off'}
          </span>
          <Form.Check
            type="switch"
            id="auto-restart-enabled"
            className="auto-restart__switch"
            checked={enabled}
            aria-label="Enable automatic restart"
            onChange={(e) => entity.setAutoRestartEnabled(e.target.checked)}
          />
        </span>
      </header>

      {enabled && view ? (
        <div className="auto-restart__body">
          <Info>{renderSummary(view.summary)}</Info>

          {view.timeline && <Timeline timeline={view.timeline} />}

          <Field
            label={
              <>
                Only restart on return codes <span className="auto-restart__optional">(optional)</span>
              </>
            }
            helpText={
              <>
                Leave empty to restart on <strong>any</strong> failure. Add one or more exit codes to restrict.
              </>
            }
          >
            <ReturnCodeChips codes={entity.restartOnReturnCodes} onChange={entity.setRestartOnReturnCodes} ariaLabel="Only restart on return codes" />
          </Field>

          <div className="auto-restart__grid">
            <Field label="Max auto-restarts" helpText="0 = don't retry">
              <EditNumber value={entity.autoRestartAmount} min={0} onChange={(v) => entity.setAutoRestartAmount(v ?? 0)} />
            </Field>
            <Field label="Delay between retries (seconds)" helpText="Wait before each retry">
              <EditNumber value={entity.autoRestartDelaySeconds} min={0} onChange={(v) => entity.setAutoRestartDelaySeconds(v ?? 0)} />
            </Field>
          </div>
        </div>
      ) : (
        <div className="auto-restart__off-note">Automatic restart is off — a failed run is final and no retry is attempted.</div>
      )}
    </div>
  );
});
